import api from './api';
import { addMinutes } from 'date-fns/esm';
import { getUTCISOString } from '../collums-components/helpers/timezone';
import { getLocationItem } from '../collums-constants/utils';

const createLinkedAppointments = (currentTime, currentAppointments, currentLocation, currentStaff, courseContext) => {
    const appointmentItems = currentAppointments;

    // If we only have one service appointment, no linked appointments needed
    if (appointmentItems.length <= 1) {
        return [];
    }

    // eslint-disable-next-line
    let firstServiceStartTime = new Date(currentTime.getTime());
    let otherServicesDuration = 0;
    /* eslint-disable array-callback-return */
    const linkedAppointments = appointmentItems.map((appointment, index) => {
        const startTime = addMinutes(firstServiceStartTime, otherServicesDuration);
        const currentAppointmentDuration =
            appointment.staff?.duration ||
            getLocationItem(appointment.service, currentLocation.id)?.defaultDuration ||
            0;

        otherServicesDuration += Number(currentAppointmentDuration);
        const endTime = addMinutes(startTime, currentAppointmentDuration);

        if (index !== 0) {
            const serviceId = appointment.service?.id || appointment.service?._id;
            const isCourseServiceId =
                courseContext?.courseServices && serviceId && courseContext.courseServices.includes(serviceId);
            const isRegularServiceBooking = appointment.service?._isRegularServiceBooking;
            const isCourseService = isCourseServiceId && !isRegularServiceBooking;

            const appointmentData = {
                event: {
                    start: getUTCISOString(startTime),
                    end: getUTCISOString(endTime)
                },
                price: isCourseService ? 0 : appointment.staff?.grossPrice || appointment.service?.defaultPrice,
                clinic: currentLocation.id,
                practitioner: appointment.staff?.id || '',
                practitioners: appointment.staff ? [appointment.staff.id] : [],
                service: appointment.service.id,
                isLinked: true,
                type: 'Appointment'
            };

            if (isCourseService) {
                appointmentData.course = courseContext.courseId;
            }

            return appointmentData;
        }
    });
    /* eslint-enable array-callback-return */

    return linkedAppointments.filter(appt => appt !== undefined);
};

class AppointmentApi {
    static getAvailableDays(data) {
        return api
            .request({
                method: 'post',
                url: '/appointments/days-available-by-month',
                data
            })
            .then(res => res)
            .catch(console.error);
    }

    static getAppointmentsCustomer(customerId) {
        return api
            .request({
                method: 'get',
                url: `/appointments/customer/${customerId}?pageSize=${100}&group=ALL`
            })
            .then(res => res)
            .catch(console.error);
    }

    static saveAppointment(
        { currentAppointments, currentLocation, currentTime, currentStaff, cardInfo, deposit, courseContext },
        coherentTransactionId
    ) {
        // const coursePurchaseAppointments = currentAppointments.filter(appt => {
        //     const serviceId = appt.service?.id || appt.service?._id;
        //     const isCourseService =
        //         courseContext?.courseServices && serviceId && courseContext.courseServices.includes(serviceId);
        //     return isCourseService;
        // });

        // if (coursePurchaseAppointments.length > 0 && coursePurchaseAppointments.length === currentAppointments.length) {
        //     // All appointments are course-related - handle course purchase logic here if needed
        // }

        const allAppointments = currentAppointments;

        const staff =
            currentStaff && Array.isArray(currentStaff)
                ? currentStaff.length > 0
                    ? currentStaff[0]
                    : null
                : currentStaff;

        const firstAppointment = allAppointments[0];
        const firstServiceId = firstAppointment.service?.id || firstAppointment.service?._id;
        const isFirstAppointmentCourseService =
            courseContext?.courseServices && firstServiceId && courseContext.courseServices.includes(firstServiceId);
        const isFirstAppointmentRegularBooking = firstAppointment.service?._isRegularServiceBooking;
        const isFirstAppointmentCourse = isFirstAppointmentCourseService && !isFirstAppointmentRegularBooking;

        const appointmentDuration =
            firstAppointment.staff?.duration ||
            getLocationItem(firstAppointment.service, currentLocation.id)?.defaultDuration ||
            firstAppointment.service.duration ||
            0;

        let startEvent = addMinutes(currentTime, 0);
        let endEvent = addMinutes(startEvent, appointmentDuration);

        const servicelocation =
            firstAppointment.service.servicelocations?.find(location => location.location === currentLocation.id) || {};

        let appointmentPrice = 0;
        if (!isFirstAppointmentCourse) {
            appointmentPrice =
                firstAppointment.staff?.grossPrice ??
                servicelocation.grossPrice ??
                firstAppointment.service.defaultPrice ??
                0;
        }

        const data = {
            appointment: {
                event: {
                    start: getUTCISOString(startEvent),
                    end: getUTCISOString(endEvent)
                },
                price: appointmentPrice,
                tax: servicelocation.taxType || firstAppointment.service.taxType,
                grossPrice: appointmentPrice,
                netPrice: isFirstAppointmentCourse
                    ? 0
                    : firstAppointment.staff?.netPrice ||
                      servicelocation.netPrice ||
                      firstAppointment.service.defaultNetPrice,
                practitioner: staff?.id || '',
                clinic: currentLocation.id,
                service: firstAppointment.service.id || firstAppointment.service._id,
                deposit: typeof deposit === 'number' ? deposit : 0,
                ...(isFirstAppointmentCourse && {
                    course: courseContext.courseId
                }),
                linkedAppointments: createLinkedAppointments(
                    currentTime,
                    allAppointments,
                    currentLocation,
                    currentStaff,
                    courseContext
                )
            },
            paymentDetails: cardInfo,
            coherentTransactionId
        };

        return api
            .request({
                method: 'post',
                url: '/appointments/public',
                data
            })
            .then(res => {
                return res;
            })
            .catch(err => {
                if (err.response && err.response.data && err.response.data.message) {
                    throw new Error(err.response.data.message);
                }
                if (err.data && err.data.message) {
                    throw new Error(err.data.message);
                }
                if (err.message) {
                    throw new Error(err.message);
                }
                throw new Error('An unexpected error occurred');
            });
    }

    static getAppointment(id) {
        return api.request({
            method: 'GET',
            url: `/appointments/${id}`
        });
    }

    static removeAppointment(appointmentId) {
        return api.request({
            method: 'PUT',
            url: `/appointments/cancel-by-customer/${appointmentId}`
        });
    }

    static deletePendingAppointments(appointmentId) {
        return api.request({
            method: 'PUT',
            url: `/appointments/delete-pending/${appointmentId}`
        });
    }

    static rescheduleAppointment(id, { currentTime, currentAppointment, currentStaff, currentLocation }) {
        const startEvent = currentTime;
        let appointmentDuration = 0;

        if (currentAppointment.service?.salePrice === undefined) {
            appointmentDuration =
                currentAppointment.staff?.duration || currentAppointment.service?.defaultDuration || 0;
        }

        const endEvent = addMinutes(currentTime, appointmentDuration);
        const staff = currentAppointment.staff || currentStaff;
        return api
            .request({
                method: 'PUT',
                url: `/appointments/reschedule/${id}`,
                data: {
                    appointment: {
                        event: {
                            start: getUTCISOString(startEvent),
                            end: getUTCISOString(endEvent)
                        },
                        price: staff.grossPrice,
                        netPrice: staff.netPrice,
                        grossPrice: staff.grossPrice,
                        practitioner: staff.id,
                        clinic: currentLocation.id,
                        service: currentAppointment.service.id,
                        linkedAppointments: currentAppointment.linkedAppointments
                    }
                }
            })
            .then(res => res)
            .catch(err => {
                if (err.response && err.response.data && err.response.data.message) {
                    throw new Error(err.response.data.message);
                }
                if (err.data && err.data.message) {
                    throw new Error(err.data.message);
                }
                if (err.message) {
                    throw new Error(err.message);
                }
                throw new Error('An unexpected error occurred');
            });
    }
}
export default AppointmentApi;
